/**
 * Auth Store
 * Pinia store for authentication state management
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getTokens, clearTokens, storeTokens, isTokenExpired, getIdToken, } from '../services/token-storage.service.js';
import { extractUserFromIdToken } from '../utils/jwt-decoder.js';
export const useAuthStore = defineStore('auth', () => {
    // State
    const user = ref(null);
    const isAuthenticated = ref(false);
    const isTokenValid = ref(false); // Changed from computed to ref for explicit control
    const isLoading = ref(false);
    const error = ref(null);
    // Getters
    const accessToken = computed(() => {
        const tokens = getTokens();
        return tokens?.accessToken || null;
    });
    const refreshToken = computed(() => {
        const tokens = getTokens();
        return tokens?.refreshToken || null;
    });
    // Helper function to check and update isTokenValid
    function checkTokenValidity() {
        const tokens = getTokens();
        const expired = isTokenExpired();
        const isValid = tokens !== null && !expired;
        isTokenValid.value = isValid;
        return isValid;
    }
    const userRoles = computed(() => {
        return user.value?.roles || [];
    });
    const userGroups = computed(() => {
        return user.value?.groups || [];
    });
    // Actions
    function setUser(userData) {
        user.value = userData;
        // Don't override isAuthenticated if tokens are set - setTokens already set it
        if (userData === null) {
            isAuthenticated.value = false;
        }
        // If userData is set, keep isAuthenticated as is (should already be true from setTokens)
    }
    function setTokens(tokens) {
        try {
            storeTokens(tokens);
            isAuthenticated.value = true;
            // Directly check and set isTokenValid
            const tokensAfterStore = getTokens();
            const expiredAfterStore = isTokenExpired();
            const isValidDirect = tokensAfterStore !== null && !expiredAfterStore;
            isTokenValid.value = isValidDirect;
            // Also call checkTokenValidity for consistency
            checkTokenValidity();
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to store tokens';
            throw err;
        }
    }
    function setLoading(loading) {
        isLoading.value = loading;
    }
    function setError(err) {
        error.value = err;
    }
    function clearAuth() {
        user.value = null;
        isAuthenticated.value = false;
        isTokenValid.value = false;
        error.value = null;
        clearTokens();
    }
    function initializeAuth() {
        const tokens = getTokens();
        const expired = isTokenExpired();
        if (tokens && !expired) {
            if (!isAuthenticated.value) {
                isAuthenticated.value = true;
            }
            // Directly set isTokenValid (same approach as setTokens)
            isTokenValid.value = true;
            // Also call checkTokenValidity for consistency
            checkTokenValidity();
            if (!user.value) {
                const idToken = getIdToken();
                if (idToken) {
                    const userInfo = extractUserFromIdToken(idToken);
                    if (userInfo) {
                        setUser({
                            id: userInfo.id,
                            email: userInfo.email,
                            username: userInfo.username,
                            name: userInfo.name,
                            emailVerified: true,
                            groups: userInfo.groups || [],
                            roles: [],
                        });
                    }
                }
            }
        }
        else {
            // Double-check tokens one more time before clearing (in case of timing issues)
            const doubleCheckTokens = getTokens();
            const doubleCheckExpired = isTokenExpired();
            if (doubleCheckTokens && !doubleCheckExpired) {
                // Tokens are actually valid, restore auth
                isAuthenticated.value = true;
                isTokenValid.value = true;
                checkTokenValidity();
            }
            else if (!isAuthenticated.value) {
                clearAuth();
            }
        }
    }
    return {
        // State
        user,
        isAuthenticated,
        isLoading,
        error,
        // Getters
        accessToken,
        refreshToken,
        isTokenValid,
        userRoles,
        userGroups,
        // Actions
        setUser,
        setTokens,
        setLoading,
        setError,
        clearAuth,
        initializeAuth,
    };
});
//# sourceMappingURL=auth.store.js.map